// client/src/components/pages/Settings/RoleManagement.tsx (converted from .jsx, ST-00222)
//
// Settings → Roles & Permissions.
//   - IAM-23: a role has ONE name. The old form had "Role Name" (said to be fixed) and
//     "Display Name"; the server stored the display name and edits renamed the role.
//     Now one "Role name" field, editable for custom roles, sent as { roleName }.
//   - IAM-17: a built-in (system) role's permissions are shared by every organisation,
//     so only a global admin may change them; administrator and global-admin always
//     have full access.
//   - ST-00224: each change follows its permission key (administrators hold them all):
//       roles.create              Create Role
//       roles.edit                Edit (rename, description) of a custom role
//       roles.delete              Delete a custom role whose permissions you all hold
//                                 (the server's `assignable`)
//       roles.assign_permissions  the permission checkboxes; without full access you can
//                                 add or remove only permissions you hold yourself
//       roles.manage              all four
//     The page opens with roles.view or any of those keys; without any it is read-only.
//   - errors show the server's message (a name clash is a 409 that says why).
import { useState, useEffect, useCallback, type ChangeEvent, type FormEvent } from 'react';
import { FaPlus, FaEdit, FaTrash, FaLock, FaShieldAlt, FaUsers } from 'react-icons/fa';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import type {
  PermissionOption,
  RoleDetail,
  RoleSummary,
  SettingsApiError,
} from '../../../types/clientPages/roleManagement';

const FULL_ACCESS_ROLES: readonly string[] = ['administrator', 'global-admin'];
const ROLE_NAME_MAX = 100;

const serverMessage = (err: unknown, fallback: string): string =>
  (err as SettingsApiError)?.response?.data?.message || fallback;

/** How the caller may treat a role's permissions. */
interface PermissionAccess {
  readOnly: boolean;
  /** Shown above the list. */
  note: string | null;
  /** administrator / global-admin: no list, they always have every permission. */
  fullAccess: boolean;
  /**
   * Whether the caller may tick or untick this permission (ST-00224): without full
   * access only the permissions the caller holds.
   */
  canChange: (permissionKey: string) => boolean;
}

const ANY_PERMISSION = (): boolean => true;

// ─── Role Create/Edit Modal ────────────────────────────────────────────────────
interface RoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void | Promise<void>;
  role: RoleSummary | null;
}

const RoleModal = ({ isOpen, onClose, onSave, role }: RoleModalProps) => {
  const [roleName, setRoleName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    setRoleName(role?.role_name || '');
    setDescription(role?.description || '');
    setFormError(null);
  }, [role, isOpen]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const name = roleName.trim();
    if (!name) {
      setFormError('A role name is required.');
      return;
    }
    try {
      setSaving(true);
      setFormError(null);
      const payload = { roleName: name, description: description.trim() };
      if (role) {
        await api.roles.update(role.role_id, payload);
      } else {
        await api.roles.create(payload);
      }
      await onSave();
    } catch (err) {
      console.error('Error saving role:', err);
      setFormError(serverMessage(err, 'Failed to save role. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-surface rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900">{role ? 'Edit Role' : 'Create Role'}</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {formError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {formError}
            </div>
          )}

          <div>
            <label htmlFor="role-name" className="block text-sm font-medium text-slate-700 mb-1">
              Role name <span className="text-red-500">*</span>
            </label>
            <input
              id="role-name"
              type="text"
              name="roleName"
              value={roleName}
              maxLength={ROLE_NAME_MAX}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setRoleName(e.target.value)}
              placeholder="e.g. Weighbridge clerks"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="text-xs text-slate-500 mt-1">
              Shown in User Management. Must be unique across LexiBridge.
            </p>
          </div>

          <div>
            <label
              htmlFor="role-description"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              Description
            </label>
            <textarea
              id="role-description"
              name="description"
              value={description}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
              rows={3}
              placeholder="Describe what this role is for..."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-all duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : role ? 'Update Role' : 'Create Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Permission Assignment Modal ────────────────────────────────────────────────
interface PermissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void | Promise<void>;
  role: RoleSummary | null;
  access: PermissionAccess;
}

const PermissionModal = ({ isOpen, onClose, onSave, role, access }: PermissionModalProps) => {
  const [allPermissions, setAllPermissions] = useState<PermissionOption[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { readOnly, note, fullAccess, canChange } = access;
  const changeable = (perm: PermissionOption): boolean =>
    !readOnly && canChange(perm.permission_key);

  const loadData = useCallback(async (target: RoleSummary) => {
    try {
      setLoading(true);
      setError(null);
      const [permissionsRes, roleDetailRes] = await Promise.all([
        api.roles.getAllPermissions(),
        api.roles.get(target.role_id),
      ]);

      const permissions = (permissionsRes.data ?? permissionsRes) as PermissionOption[];
      const roleDetail = (roleDetailRes.data ?? roleDetailRes) as RoleDetail;

      setAllPermissions(Array.isArray(permissions) ? permissions : []);
      setSelectedIds(new Set((roleDetail.permissions || []).map((p) => p.permission_id)));
    } catch (err) {
      console.error('Error loading permissions:', err);
      setError(serverMessage(err, 'Failed to load permissions. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && role && !fullAccess) {
      void loadData(role);
    }
  }, [isOpen, role, fullAccess, loadData]);

  const handleToggle = (perm: PermissionOption) => {
    if (!changeable(perm)) return;
    const permId = perm.permission_id;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(permId)) {
        next.delete(permId);
      } else {
        next.add(permId);
      }
      return next;
    });
  };

  const handleToggleCategory = (categoryPermissions: PermissionOption[]) => {
    // Only the permissions the caller may change (ST-00224); the others keep their state.
    const own = categoryPermissions.filter(changeable);
    if (own.length === 0) return;
    const allSelected = own.every((p) => selectedIds.has(p.permission_id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      own.forEach((p) => {
        if (allSelected) {
          next.delete(p.permission_id);
        } else {
          next.add(p.permission_id);
        }
      });
      return next;
    });
  };

  const handleSave = async () => {
    if (!role || readOnly) return;
    try {
      setSaving(true);
      setError(null);
      await api.roles.assignPermissions(role.role_id, Array.from(selectedIds));
      await onSave();
    } catch (err) {
      console.error('Error assigning permissions:', err);
      setError(serverMessage(err, 'Failed to save permissions. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !role) return null;

  // Group permissions by Category
  const grouped = allPermissions.reduce<Record<string, PermissionOption[]>>((acc, perm) => {
    const cat = perm.category || 'Uncategorized';
    (acc[cat] ??= []).push(perm);
    return acc;
  }, {});
  const sortedCategories = Object.keys(grouped).sort();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-surface rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <h2 className="text-lg font-bold text-slate-900">
            {readOnly ? 'Permissions' : 'Manage Permissions'} &mdash; {role.role_name}
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            {readOnly
              ? 'The permissions this role grants.'
              : 'Select the permissions to assign to this role.'}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {note && (
            <div className="bg-slate-50 border border-slate-200 text-slate-700 px-4 py-3 rounded-lg text-sm mb-4">
              {note}
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
              {error}
            </div>
          )}

          {fullAccess ? null : loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-slate-200 border-t-primary-500"></div>
              <p className="mt-4 text-slate-600">Loading permissions...</p>
            </div>
          ) : sortedCategories.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600">No permissions available.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {sortedCategories.map((category) => {
                const perms = grouped[category] ?? [];
                const allChecked = perms.every((p) => selectedIds.has(p.permission_id));
                const someChecked = perms.some((p) => selectedIds.has(p.permission_id));
                const categoryChangeable = perms.some(changeable);
                return (
                  <div
                    key={category}
                    className="border border-slate-200 rounded-lg overflow-hidden"
                  >
                    <div
                      className={`flex items-center gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200 select-none ${categoryChangeable ? 'cursor-pointer' : ''}`}
                      onClick={() => handleToggleCategory(perms)}
                    >
                      <input
                        type="checkbox"
                        aria-label={`All of ${category}`}
                        checked={allChecked}
                        disabled={!categoryChangeable}
                        ref={(el) => {
                          if (el) el.indeterminate = someChecked && !allChecked;
                        }}
                        onChange={() => handleToggleCategory(perms)}
                        onClick={(e) => e.stopPropagation()}
                        className="h-4 w-4 rounded border-slate-300 text-primary-500 focus:ring-primary-500"
                      />
                      <span className="text-sm font-semibold text-slate-800">{category}</span>
                      <span className="text-xs text-slate-500 ml-auto">
                        {perms.filter((p) => selectedIds.has(p.permission_id)).length}/
                        {perms.length}
                      </span>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {perms.map((perm) => {
                        const canToggle = changeable(perm);
                        // Editable modal, but a permission the caller does not hold (ST-00224).
                        const notHeld = !readOnly && !canToggle;
                        return (
                          <label
                            key={perm.permission_id}
                            className={`flex items-start gap-3 px-4 py-3 transition-colors ${canToggle ? 'hover:bg-slate-50 cursor-pointer' : ''}`}
                            title={notHeld ? 'You do not have this permission' : undefined}
                          >
                            <input
                              type="checkbox"
                              checked={selectedIds.has(perm.permission_id)}
                              disabled={!canToggle}
                              onChange={() => handleToggle(perm)}
                              className="h-4 w-4 mt-0.5 rounded border-slate-300 text-primary-500 focus:ring-primary-500"
                            />
                            <div className="flex-1 min-w-0">
                              <div
                                className={`text-sm font-medium ${notHeld ? 'text-slate-400' : 'text-slate-800'}`}
                              >
                                {perm.permission_name}
                              </div>
                              {notHeld && (
                                <div className="text-xs text-slate-400 italic mt-0.5">
                                  You do not have this permission
                                </div>
                              )}
                              {perm.description && (
                                <div className="text-xs text-slate-500 mt-0.5">
                                  {perm.description}
                                </div>
                              )}
                              {perm.page_route && (
                                <div className="text-xs text-slate-400 mt-0.5 font-mono">
                                  {perm.page_route}
                                </div>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-all duration-200"
          >
            {readOnly ? 'Close' : 'Cancel'}
          </button>
          {!readOnly && (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || loading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Permissions'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Role Card ──────────────────────────────────────────────────────────────────
interface RoleCardProps {
  role: RoleSummary;
  /** The caller may rename it (roles.edit / roles.manage; custom roles only). */
  canEdit: boolean;
  /** The caller may delete it (roles.delete / roles.manage, a role within their access). */
  canDelete: boolean;
  /** The caller may change its permissions. */
  canEditPermissions: boolean;
  onEdit: (role: RoleSummary) => void;
  onDelete: (role: RoleSummary) => void;
  onManagePermissions: (role: RoleSummary) => void;
}

const RoleCard = ({
  role,
  canEdit,
  canDelete,
  canEditPermissions,
  onEdit,
  onDelete,
  onManagePermissions,
}: RoleCardProps) => {
  const isSystem = role.is_system_role;

  return (
    <div
      role="group"
      aria-label={`${role.role_name} role`}
      className="bg-surface border border-slate-200 rounded-lg p-5 hover:shadow-md transition-shadow duration-200"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {isSystem ? (
              <FaLock className="text-slate-400 flex-shrink-0" />
            ) : (
              <FaShieldAlt className="text-primary-500 flex-shrink-0" />
            )}
            <h3 className="text-base font-semibold text-slate-900 truncate">{role.role_name}</h3>
            {isSystem && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 flex-shrink-0">
                System
              </span>
            )}
          </div>
          {role.description && (
            <p className="text-sm text-slate-600 mt-1 line-clamp-2">{role.description}</p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 mt-4">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
          <FaUsers className="text-xs" />
          {role.user_count ?? 0} {role.user_count === 1 ? 'User' : 'Users'}
        </span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700">
          <FaShieldAlt className="text-xs" />
          {role.permission_count ?? 0} {role.permission_count === 1 ? 'Permission' : 'Permissions'}
        </span>
      </div>

      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100">
        <button
          onClick={() => onManagePermissions(role)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-all duration-200 font-medium"
        >
          <FaShieldAlt className="text-xs" />
          {canEditPermissions ? 'Permissions' : 'View permissions'}
        </button>
        {canEdit && !isSystem && (
          <button
            onClick={() => onEdit(role)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-all duration-200 font-medium"
          >
            <FaEdit className="text-xs" />
            Edit
          </button>
        )}
        {canDelete && !isSystem && (
          <button
            onClick={() => onDelete(role)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-all duration-200 font-medium"
          >
            <FaTrash className="text-xs" />
            Delete
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────────
const RoleManagement = () => {
  const { hasAppRole, hasPermission } = useAuth();
  const isGlobalAdmin = hasAppRole('global-admin');
  // ST-00224: each change needs its key; roles.manage stands for all four. Administrators
  // hold '*' (every key, and every permission to hand out).
  const hasFullAccess = hasPermission('*');
  const canManageRoles = hasPermission('roles.manage');
  const canCreate = canManageRoles || hasPermission('roles.create');
  const canRename = canManageRoles || hasPermission('roles.edit');
  const canDelete = canManageRoles || hasPermission('roles.delete');
  const canAssignPermissions = canManageRoles || hasPermission('roles.assign_permissions');
  const hasRoleActions = canCreate || canRename || canDelete || canAssignPermissions;

  const [roles, setRoles] = useState<RoleSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Role modal state
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<RoleSummary | null>(null);

  // Permission modal state
  const [isPermModalOpen, setIsPermModalOpen] = useState(false);
  const [permissionRole, setPermissionRole] = useState<RoleSummary | null>(null);

  const fetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.roles.getAll();
      const data = (response.data ?? response) as RoleSummary[];
      setRoles(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching roles:', err);
      setError(serverMessage(err, 'Failed to load roles. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchRoles();
  }, [fetchRoles]);

  /** What the caller may do with a role's permissions (mirrors server/routes/roles.ts). */
  const permissionAccess = (role: RoleSummary | null): PermissionAccess => {
    if (role && FULL_ACCESS_ROLES.includes(role.role_name)) {
      return {
        readOnly: true,
        note: 'This role always has full access.',
        fullAccess: true,
        canChange: ANY_PERMISSION,
      };
    }
    if (!canAssignPermissions) {
      return { readOnly: true, note: null, fullAccess: false, canChange: ANY_PERMISSION };
    }
    if (role?.is_system_role && !isGlobalAdmin) {
      return {
        readOnly: true,
        note: 'Built-in roles are shared by every organisation; only Lex-Adapt can change their permissions.',
        fullAccess: false,
        canChange: ANY_PERMISSION,
      };
    }
    if (hasFullAccess) {
      return { readOnly: false, note: null, fullAccess: false, canChange: ANY_PERMISSION };
    }
    return {
      readOnly: false,
      note: 'You can add or remove only the permissions you hold yourself.',
      fullAccess: false,
      canChange: hasPermission,
    };
  };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleCreateRole = () => {
    setEditingRole(null);
    setIsRoleModalOpen(true);
  };

  const handleEditRole = (role: RoleSummary) => {
    setEditingRole(role);
    setIsRoleModalOpen(true);
  };

  const handleDeleteRole = async (role: RoleSummary) => {
    const userWarning =
      (role.user_count ?? 0) > 0
        ? `\n\nWarning: This role is currently assigned to ${role.user_count} user(s). They will lose the permissions associated with this role.`
        : '';
    if (
      window.confirm(`Are you sure you want to delete the role "${role.role_name}"?${userWarning}`)
    ) {
      try {
        await api.roles.delete(role.role_id);
        await fetchRoles();
      } catch (err) {
        console.error('Error deleting role:', err);
        setError(serverMessage(err, 'Failed to delete role. Please try again.'));
      }
    }
  };

  const handleManagePermissions = (role: RoleSummary) => {
    setPermissionRole(role);
    setIsPermModalOpen(true);
  };

  const handleRoleModalClose = () => {
    setIsRoleModalOpen(false);
    setEditingRole(null);
  };

  const handleRoleModalSave = async () => {
    await fetchRoles();
    handleRoleModalClose();
  };

  const handlePermModalClose = () => {
    setIsPermModalOpen(false);
    setPermissionRole(null);
  };

  const handlePermModalSave = async () => {
    await fetchRoles();
    handlePermModalClose();
  };

  // ── Derived data ──────────────────────────────────────────────────────────
  const systemRoles = roles.filter((r) => r.is_system_role);
  const customRoles = roles.filter((r) => !r.is_system_role);

  const renderCard = (role: RoleSummary) => (
    <RoleCard
      key={role.role_id}
      role={role}
      canEdit={canRename}
      canDelete={canDelete && role.assignable !== false}
      canEditPermissions={!permissionAccess(role).readOnly}
      onEdit={handleEditRole}
      onDelete={handleDeleteRole}
      onManagePermissions={handleManagePermissions}
    />
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header Section */}
      <div className="sticky top-0 z-10 bg-surface border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Role Management</h1>
            <p className="text-slate-600 text-sm mt-1">
              {hasRoleActions
                ? 'Manage roles and their permissions for your organisation'
                : 'You can view roles and their permissions. You do not have permission to change them.'}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start">
            <svg
              className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1">
              <p className="font-medium">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              aria-label="Dismiss"
              className="flex-shrink-0 ml-4 text-red-400 hover:text-red-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-primary-500"></div>
            <p className="mt-4 text-slate-600">Loading roles...</p>
          </div>
        ) : roles.length === 0 ? (
          /* Empty State */
          <div className="bg-surface rounded-xl border border-slate-200 shadow-sm">
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-4">
                <FaShieldAlt className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-medium text-slate-900 mb-2">No Roles Found</h3>
              {canCreate && (
                <>
                  <p className="text-slate-600 mb-6 max-w-md mx-auto">
                    Get started by creating your first custom role to manage permissions within your
                    organisation.
                  </p>
                  <button
                    onClick={handleCreateRole}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-all duration-200 shadow-sm hover:shadow-md font-medium"
                  >
                    <FaPlus className="text-sm" />
                    Create Your First Role
                  </button>
                </>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* System Roles Section */}
            {systemRoles.length > 0 && (
              <div className="bg-surface rounded-xl border border-slate-200 shadow-sm">
                <div className="px-6 py-4 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    <FaLock className="text-slate-400" />
                    <div>
                      <h2 className="text-lg font-bold text-slate-900">System Roles</h2>
                      <p className="text-sm text-slate-600 mt-0.5">
                        Built-in roles shared by every organisation. They cannot be renamed or
                        deleted.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {systemRoles.map(renderCard)}
                  </div>
                </div>
              </div>
            )}

            {/* Custom Roles Section */}
            <div className="bg-surface rounded-xl border border-slate-200 shadow-sm">
              <div className="px-6 py-4 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FaShieldAlt className="text-primary-500" />
                    <div>
                      <h2 className="text-lg font-bold text-slate-900">Custom Roles</h2>
                      <p className="text-sm text-slate-600 mt-0.5">
                        {hasRoleActions
                          ? 'Create and manage custom roles tailored to your organisation'
                          : 'Roles made by your organisation'}
                      </p>
                    </div>
                  </div>
                  {canCreate && (
                    <button
                      onClick={handleCreateRole}
                      className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-all duration-200 shadow-sm hover:shadow-md font-medium"
                    >
                      <FaPlus className="text-sm" />
                      Create Role
                    </button>
                  )}
                </div>
              </div>
              <div className="p-6">
                {customRoles.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-slate-100 mb-3">
                      <FaShieldAlt className="w-6 h-6 text-slate-400" />
                    </div>
                    <h3 className="text-base font-medium text-slate-900 mb-1">No Custom Roles</h3>
                    {canCreate && (
                      <>
                        <p className="text-sm text-slate-600 mb-4">
                          Create a custom role to define specific permissions for your team.
                        </p>
                        <button
                          onClick={handleCreateRole}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-all duration-200 shadow-sm hover:shadow-md font-medium text-sm"
                        >
                          <FaPlus className="text-xs" />
                          Create Role
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {customRoles.map(renderCard)}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Role Create/Edit Modal */}
      {(canCreate || canRename) && (
        <RoleModal
          isOpen={isRoleModalOpen}
          onClose={handleRoleModalClose}
          onSave={handleRoleModalSave}
          role={editingRole}
        />
      )}

      {/* Permission Assignment Modal */}
      <PermissionModal
        isOpen={isPermModalOpen}
        onClose={handlePermModalClose}
        onSave={handlePermModalSave}
        role={permissionRole}
        access={permissionAccess(permissionRole)}
      />
    </div>
  );
};

export default RoleManagement;
